import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/components/ui/use-toast";
import {
  deleteProgramNewsReview,
  saveProgramNewsReview,
  type ProgramNewsDeliveryClass,
  type ProgramNewsReview,
  type ProgramNewsReviewInput,
  type ProgramNewsReviewVerdict,
} from "@/services/adminProgramNews";
import ChoiceButtons from "./ChoiceButtons";
import OtherReviews from "./OtherReviews";
import ReviewTagChips from "./ReviewTagChips";
import { CRITIQUE_MAX, splitReviews } from "./reviewHelpers";

interface ReviewPanelProps {
  targetType: "update" | "item";
  targetId: string;
  reviews: ProgramNewsReview[];
  myEmail?: string | null;
  /** True for articles the rules threw out. */
  suppressed?: boolean;
  /** Called after a save or remove so the parent can refresh. */
  onChanged: () => void;
}

const VERDICTS: Array<{ value: ProgramNewsReviewVerdict; label: string }> = [
  { value: "right", label: "Right call" },
  { value: "wrong", label: "Wrong call" },
];

const CLASSES: Array<{ value: ProgramNewsDeliveryClass; label: string }> = [
  { value: "alert", label: "Alert" },
  { value: "digest", label: "Digest" },
  { value: "ignore", label: "Ignore" },
];

const PLACEHOLDER = 'What went wrong? e.g. "Last season\'s news", "Wrong gender"';

const ReviewPanel = ({
  targetType,
  targetId,
  reviews,
  myEmail,
  suppressed = false,
  onChanged,
}: ReviewPanelProps) => {
  const { toast } = useToast();
  const { mine, others } = splitReviews(reviews, myEmail);
  const [verdict, setVerdict] = useState<ProgramNewsReviewVerdict | null>(mine?.verdict ?? null);
  const [correctClass, setCorrectClass] = useState<ProgramNewsDeliveryClass | null>(
    mine?.correctClass ?? null
  );
  const [tags, setTags] = useState<string[]>(mine?.tags ?? []);
  const [critique, setCritique] = useState(mine?.critique ?? "");
  const [busy, setBusy] = useState(false);

  const mineId = mine?._id;
  const mineStamp = mine?.updatedAt;
  useEffect(() => {
    setVerdict(mine?.verdict ?? null);
    setCorrectClass(mine?.correctClass ?? null);
    setTags(mine?.tags ?? []);
    setCritique(mine?.critique ?? "");
    // Re-seed only when the stored review itself changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mineId, mineStamp]);

  const valid = verdict === "right" || (verdict === "wrong" && correctClass !== null);

  const toggleTag = (tag: string) =>
    setTags(current =>
      current.includes(tag) ? current.filter(t => t !== tag) : [...current, tag]
    );

  const save = async () => {
    if (!verdict || !valid) return;
    const input: ProgramNewsReviewInput = { targetType, targetId, verdict };
    if (verdict === "wrong" && correctClass) {
      input.correctClass = correctClass;
      if (tags.length) input.tags = tags;
    }
    if (critique.trim()) input.critique = critique.trim();
    try {
      setBusy(true);
      await saveProgramNewsReview(input);
      toast({ title: "Review saved" });
      onChanged();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : String(err);
      toast({ title: "Failed to save review", description: message, variant: "destructive" });
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!mine) return;
    try {
      setBusy(true);
      await deleteProgramNewsReview(mine._id);
      toast({ title: "Review removed" });
      onChanged();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : String(err);
      toast({ title: "Failed to remove review", description: message, variant: "destructive" });
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="space-y-4 rounded-md border bg-muted/30 p-4">
      <div className="flex items-center justify-between gap-2">
        <h4 className="font-medium">Was this the right call?</h4>
        {mine ? <span className="text-xs text-muted-foreground">You reviewed this</span> : null}
      </div>
      <ChoiceButtons<ProgramNewsReviewVerdict>
        label="Verdict"
        options={VERDICTS}
        value={verdict}
        onChange={setVerdict}
      />

      {verdict === "wrong" ? (
        <div className="space-y-3">
          <div className="space-y-2">
            <Label>What should it have been?</Label>
            <ChoiceButtons<ProgramNewsDeliveryClass>
              label="What should it have been?"
              options={CLASSES}
              value={correctClass}
              onChange={setCorrectClass}
            />
          </div>
          <div className="space-y-2">
            <Label>What was the problem? (optional)</Label>
            <ReviewTagChips selected={tags} onToggle={toggleTag} suppressed={suppressed} />
          </div>
        </div>
      ) : null}

      <div className="space-y-1">
        <Label htmlFor={`critique-${targetId}`}>Critique</Label>
        <Textarea
          id={`critique-${targetId}`}
          value={critique}
          maxLength={CRITIQUE_MAX}
          placeholder={PLACEHOLDER}
          onChange={event => setCritique(event.target.value)}
        />
        <div className="text-right text-xs text-muted-foreground">
          {critique.length}/{CRITIQUE_MAX}
        </div>
      </div>

      <div className="flex gap-2">
        <Button type="button" onClick={save} disabled={!valid || busy}>
          Save review
        </Button>
        {mine ? (
          <Button type="button" variant="ghost" onClick={remove} disabled={busy}>
            Remove my review
          </Button>
        ) : null}
      </div>

      <OtherReviews reviews={others} />
    </section>
  );
};

export default ReviewPanel;
